/**
 * Power distribution (PDU) models shared by device mode (processor DashboardHub) and
 * cloud mode (DeviceRegistry.Api). Field names match the processor's PowerDistributionDtos.
 */

/** `AlwaysOn` is a channel without a switch (a meter-only outlet): live whenever the unit is powered. */
export type OutletState = 'Unknown' | 'Off' | 'On' | 'AlwaysOn' | 'Cycling';
export type OutletAction = 'on' | 'off' | 'toggle' | 'cycle';
export type PowerFeature = 'Switched' | 'OutletMetering' | 'TotalMetering' | 'NativeCycle';

export interface PowerReading {
    watts: number;
    volts?: number | null;
    amps?: number | null;
    powerFactor?: number | null;
    energyKWh?: number | null;
    timestampUtc?: string;
}

export interface PowerOutlet {
    /** 1-based channel number. */
    id: number;
    name: string;
    state: OutletState;
    switchable: boolean;
    metered: boolean;
    locked: boolean;
    lockReason?: string | null;
    reading?: PowerReading | null;
}

export interface PowerDevice {
    /**
     * Handle the power service uses to address this device: the runtime device id in device
     * mode, the linked-device document id in cloud mode.
     */
    key: string;
    /** Runtime device id on the processor. */
    id: number;
    uniqueId?: string | null;
    name: string;
    manufacturerName?: string | null;
    modelName?: string | null;
    online: boolean;
    features: PowerFeature[];
    webUiUrl?: string | null;
    roomName?: string | null;
    total?: PowerReading | null;
    outlets: PowerOutlet[];
}

/** Processor payload for a PDU; `key` is added by the power service. */
export type PowerDeviceDto = Omit<PowerDevice, 'key'>;

export interface OutletActionResult {
    success: boolean;
    message?: string | null;
    outlet?: PowerOutlet | null;
}

export interface OutletStatsSample {
    outletId: number;
    name: string;
    avgWatts: number;
    peakWatts: number;
    energyKWh?: number | null;
}

/** One statistics period (5 minutes by default) for one PDU. */
export interface PowerHistorySample {
    timestampUtc: string;
    periodSeconds: number;
    totalAvgWatts?: number | null;
    totalPeakWatts?: number | null;
    totalEnergyKWh?: number | null;
    outlets: OutletStatsSample[];
}

/**
 * A live change pushed by the device. Only the fields present changed; outlets are merged by id.
 */
export interface PowerUpdate {
    key: string;
    outlets?: Partial<PowerOutlet>[];
    total?: PowerReading | null;
    online?: boolean;
}

/** Apply a live update to a device snapshot, returning a new object (for signals). */
export function applyPowerUpdate(device: PowerDevice, update: PowerUpdate): PowerDevice {
    if (update.key !== device.key) return device;
    let outlets = device.outlets;
    if (update.outlets?.length) {
        const byId = new Map(update.outlets.filter((o) => o.id != null).map((o) => [o.id as number, o]));
        outlets = device.outlets.map((o) => (byId.has(o.id) ? { ...o, ...stripUndefined(byId.get(o.id)!) } : o));
    }
    return {
        ...device,
        outlets,
        total: update.total !== undefined ? update.total : device.total,
        online: update.online ?? device.online
    };
}

/**
 * True when the update is for an outlet the snapshot does not have yet, or a device it does not list. Drivers that
 * learn their outlets from the hardware (Gude, Shelly) add them after start-up, so a page loaded before that has to
 * re-read to show them.
 */
export function hasUnknownOutlet(devices: PowerDevice[], update: PowerUpdate): boolean {
    const device = devices.find((d) => d.key === update.key);
    if (!device) return !!update.outlets?.length;
    const known = new Set(device.outlets.map((o) => o.id));
    return !!update.outlets?.some((o) => o.id != null && !known.has(o.id));
}

function stripUndefined<T extends object>(value: T): Partial<T> {
    return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as Partial<T>;
}

export function isOutletOn(outlet: PowerOutlet): boolean {
    return outlet.state === 'On' || outlet.state === 'AlwaysOn';
}

export function hasFeature(device: PowerDevice, feature: PowerFeature): boolean {
    return device.features.includes(feature);
}
